import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { MoneyInput } from '@/components/ui/money-input'
import { IconPicker } from '@/components/ui/icon-picker'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { enabledCurrencyList } from '@/lib/currency'
import { useHousehold } from '@/context/HouseholdContext'
import { useT } from '@/i18n/I18nProvider'
import { useSaveAccount, type AccountInput } from '@/hooks/useAccounts'
import { defaultAccountIcon } from '@/lib/accountIcon'
import type { Account, AccountType } from '@/types/db'

const schema = z.object({
  name: z.string().min(1, 'acct.nameRequired'),
  // Alguns emojis (ex.: família 👨‍👩‍👧‍👦) são sequências ZWJ com bem mais de 4
  // code units em UTF-16 — não dá pra limitar por tamanho de string.
  icon: z.string().max(32).optional(),
  type: z.enum(['corrente', 'poupanca', 'carteira', 'cartao', 'cash', 'investimento']),
  currency: z.string().min(3),
  initial_balance: z.number({ message: 'acct.invalidValue' }),
})
type FormValues = z.infer<typeof schema>

export function AccountFormDialog({
  open,
  onOpenChange,
  account,
  defaultType = 'corrente',
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  account?: Account | null
  defaultType?: AccountType
}) {
  const { t } = useT()
  const { baseCurrency, enabledCurrencies } = useHousehold()
  const save = useSaveAccount()
  const TYPE_LABELS: Record<AccountType, string> = {
    corrente: t('acct.typeForm.corrente'),
    poupanca: t('acct.type.poupanca'),
    cash: t('acct.type.cash'),
    carteira: t('acct.type.carteira'),
    investimento: t('acct.type.investimento'),
    cartao: t('acct.type.cartao'),
  }
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      name: account?.name ?? '',
      icon: account?.icon ?? '',
      type: account?.type ?? defaultType,
      currency: account?.currency ?? baseCurrency,
      initial_balance: account?.initial_balance ?? 0,
    },
  })

  // Descarta edições abandonadas: ao reabrir, reset() re-aplica os `values` atuais.
  useEffect(() => {
    if (open) reset()
  }, [open, reset])

  async function onSubmit(values: FormValues) {
    try {
      const payload: AccountInput = { ...values, icon: values.icon?.trim() || null }
      await save.mutateAsync({ id: account?.id, values: payload })
      toast.success(account ? t('acct.updated') : t('acct.created'))
      reset()
      onOpenChange(false)
    } catch (e) {
      toast.error(t('acct.saveError'), { description: String((e as Error).message) })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>{account ? t('acct.editAccount') : t('acct.newAccount')}</DialogTitle>
            <DialogDescription>{t('acct.formDesc')}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-[80px_1fr] gap-3">
              <div className="grid gap-2">
                <Label>{t('acct.iconLabel')}</Label>
                <Controller
                  control={control}
                  name="icon"
                  render={({ field }) => (
                    <IconPicker
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      noneLabel={t('acct.iconDefault')}
                      placeholderEmoji={defaultAccountIcon(watch('type'))}
                      searchPlaceholder={t('dlg.search')}
                      aria-label={t('acct.iconLabel')}
                    />
                  )}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="name">{t('acct.nameLabel')}</Label>
                <Input id="name" {...register('name')} placeholder={t('acct.namePlaceholder')} />
                {errors.name && <p className="text-destructive text-xs">{t(errors.name.message ?? '')}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{t('acct.typeLabel')}</Label>
                <Select value={watch('type')} onValueChange={(v) => setValue('type', v as AccountType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>{t('settings.currency')}</Label>
                <Select value={watch('currency')} onValueChange={(v) => setValue('currency', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {enabledCurrencyList(enabledCurrencies).map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.code} — {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="initial_balance">{t('acct.initialBalance')}</Label>
              <Controller
                control={control}
                name="initial_balance"
                render={({ field }) => (
                  <MoneyInput
                    id="initial_balance"
                    allowNegative
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    ref={field.ref}
                  />
                )}
              />
              {errors.initial_balance && <p className="text-destructive text-xs">{t(errors.initial_balance.message ?? '')}</p>}
            </div>

          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
